import { FocusEvent, useLayoutEffect } from 'react';

import { useDeepEqualMemoValue } from '@components/component-helpers';
import type { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { hasUnavailableVariables } from '@components/text-editors/addons/field-mapping-variable-label';
import { useEvent } from '@hooks/use-event';
import { asteriskLabel } from '@utils/strings';

import { useFormContext } from './form-provider';
import { isFormError, useFormTranslations } from './locale';
import { FormRegisteredFieldData, FormValidatorFn } from './types';

type UseFormFieldData<T> = FormRegisteredFieldData<T> & {
  onChange?(value: T): void;
  onBlur?(event?: FocusEvent): void;
  label: string;
  hasResetError?: boolean;
  hideRequiredIndicator?: true;
  variableGroups?: FieldMappingGroup[];
};

export function useFormField<T>(data: UseFormFieldData<T>) {
  const {
    store: {
      actions: { register, unregister, setValue, validateField },
      useFieldError,
      useFieldValue,
      useIsSubmitting,
      useIsFormDisabled,
      useValidationMode,
    },
  } = useFormContext();
  const { t } = useFormTranslations();

  const isSubmitting = useIsSubmitting();
  const isFormDisabled = useIsFormDisabled();
  const validationMode = useValidationMode();
  const error = useFieldError(data.id);
  const value = useFieldValue<T>(data.id);
  const isRequired = data.validators?.includes('required') ?? false;
  const defaultValue = useDeepEqualMemoValue(data.defaultValue);
  const unavailableVariablesValidator = useEvent<FormValidatorFn>((fieldValue) =>
    hasUnavailableVariables(fieldValue, data.variableGroups) ? 'unavailable-variables' : true,
  );
  const validators = data.variableGroups
    ? [...(data.validators ?? []), unavailableVariablesValidator]
    : (data.validators ?? []);
  const registerData = useDeepEqualMemoValue({
    id: data.id,
    validators,
    defaultValue,
    isDisabled: data.isDisabled ?? false,
    valueType: data.valueType,
    validateOnChange: data.validateOnChange,
  });

  useLayoutEffect(() => {
    if (!registerData.id) {
      console.error('Field missing ID, unable to register.', registerData);
    }
    register(registerData);
  }, [register, registerData]);

  useLayoutEffect(() => {
    return () => {
      unregister(data.id);
    };
  }, [unregister, data.id]);

  const onChange = useEvent((newValue: T) => {
    setValue(data.id, newValue);
    data.onChange?.(newValue);
  });

  const onBlur = useEvent((event?: FocusEvent) => {
    if (validationMode === 'onBlur') {
      validateField(data.id);
    }
    data.onBlur?.(event);
  });

  return {
    id: data.id,
    isDisabled: data.isDisabled ?? (isFormDisabled || isSubmitting),
    value: value === undefined ? data.defaultValue : value,
    onChange,
    onBlur,
    label: asteriskLabel(data.label, data.hideRequiredIndicator ? false : isRequired),

    // The error could be coming from the outside, so we
    // only try to translate those that exist in the locale
    // object.
    error: data.hasResetError ? '' : error && isFormError(error) ? t(error) : error,
  };
}
